<template>
  <header class="app-header">
    <div class="app-header__container">      <div class="app-header__left">
        <div class="app-header__logo" @click="goToHome">
          <div class="app-header__logo-icon">
            <img class="app-header__logo-img" src="/logo.png" alt="Bati Vision 360 logo" />
          </div>
          </div>

        <!-- Mobile menu button -->
        <button 
          v-if="isAuthenticated && !isInProjectView && !isInTenderView && !isInApplicationView" 
          class="mobile-menu-button show-mobile" 
          @click="toggleMobileMenu"
          aria-label="Toggle navigation menu"
        >
          <svg v-if="!mobileMenuOpen" viewBox="0 0 24 24" width="24" height="24" fill="none">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="24" height="24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
          
        <!-- Show regular navigation or breadcrumb navigation based on route -->
        <nav class="app-nav hide-mobile" v-if="isAuthenticated && !isInProjectView && !isInTenderView && !isInApplicationView">
          <ul class="app-nav__list">
            <li class="app-nav__item" :class="{ 'app-nav__item--active': isDashboardActive }">
              <a @click.prevent="goToUserHome" class="app-nav__link">
                <div class="app-nav__icon">
                  <svg viewBox="0 0 20 20" width="18" height="18">
                    <path fill="currentColor" d="M10 2L3 9v11h4v-6h6v6h4V9l-7-7z" />
                  </svg>
                </div>
                <span class="app-nav__text">{{ $t('topBar.dashboard') }}</span>
              </a>
            </li>
            <PermissionGuard permission="ACCESS_PROJECT">
              <li class="app-nav__item" :class="{ 'app-nav__item--active': $route.path === '/projects' }">
                <a @click.prevent="navigateTo('/projects')" class="app-nav__link" data-testid="projects-nav-link">
                  <div class="app-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M4 3h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2zm0 2v3h12V5H4zm0 5v5h5v-5H4zm7 0v5h5v-5h-5z" />
                    </svg>
                  </div>
                  <span class="app-nav__text">{{ $t('topBar.projects') }}</span>
                </a>
              </li>
            </PermissionGuard>
            <PermissionGuard permission="ACCESS_PROJECT">
              <li class="app-nav__item" :class="{ 'app-nav__item--active': $route.path.includes('/calendar') }">
                <a @click.prevent="navigateTo('/calendar')" class="app-nav__link">
                  <div class="app-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M17 2h-1V1h-2v1H6V1H4v1H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 16H3V7h14v11zM3 5V4h14v1H3zm3 4h2v2H6V9zm4 0h2v2h-2V9zm4 0h2v2h-2V9z" />
                    </svg>
                  </div>
                  <span class="app-nav__text">{{ $t('topBar.calendar') }}</span>
                </a>
              </li>
            </PermissionGuard>
            <PermissionGuard permission="ADMIN">
              <li class="app-nav__item admin-nav-item" :class="{ 'app-nav__item--active': isAdminActive }" style="position:relative;">
                <a @click.prevent="toggleAdminMenu" class="app-nav__link" role="button" :aria-expanded="adminMenuVisible" aria-haspopup="true">
                  <div class="app-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M10 2l2.5 5 5.5.8-4 3.9.9 5.5L10 15.5 4.1 17l.9-5.5L2 7.8 7.5 7z" />
                    </svg>
                  </div>
                  <span class="app-nav__text">{{ $t('topBar.admin') }}</span>
                </a>

                <transition name="dropdown-menu">
                  <div v-if="adminMenuVisible" class="dropdown admin-dropdown" style="left:0; right:auto;">
                    <ul class="dropdown__menu">
                      <li class="dropdown__item" @click="navigateTo('/admin/subscriptions')">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        {{ $t('topBar.subscriptions') || 'Subscriptions' }}
                      </li>
                      <li class="dropdown__item" @click="navigateTo('/admin/subscriptions/firms')">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <rect x="3" y="3" width="18" height="4" fill="currentColor" />
                          <rect x="3" y="9" width="18" height="12" fill="currentColor" />
                        </svg>
                        {{ $t('admin.firmSubscriptions.menu') || 'Firm Subscriptions' }}
                      </li>
                      <li class="dropdown__item" @click="navigateTo('/admin/contact-requests')">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" d="M3 6h18v12H3z" />
                        </svg>
                        {{ $t('admin.contactRequests.menu') || 'Contact Requests' }}
                      </li>
                      <li class="dropdown__item" @click="navigateTo('/admin/users')">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5C23 14.17 18.33 13 16 13z" />
                        </svg>
                        {{ $t('topBar.users') || 'Users' }}
                      </li>
                      <li class="dropdown__item" @click="createFirmWithData" :class="{ 'dropdown__item--loading': creatingFirm }">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z" />
                        </svg>
                        {{ creatingFirm ? $t('admin.testData.creating') : $t('admin.testData.createFirm') }}
                      </li>
                    </ul>
                  </div>
                </transition>
              </li>
            </PermissionGuard>
            <PermissionGuard permission="ACCESS_TENDERS">
              <li class="app-nav__item tenders-nav-item" :class="{ 'app-nav__item--active': isTendersActive }" style="position:relative;">
                <a @click.prevent="toggleTendersMenu" class="app-nav__link" role="button" :aria-expanded="tendersMenuVisible" aria-haspopup="true">
                  <div class="app-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2zm-4 14H8v-2h2v2zm0-4H8V6h2v6zm4 4h-2v-2h2v2zm0-4h-2V6h2v6z" />
                    </svg>
                  </div>
                  <span class="app-nav__text">{{ $t('topBar.tenders') || 'Tenders' }}</span>
                </a>

                <transition name="dropdown-menu">
                  <div v-if="tendersMenuVisible" class="dropdown tenders-dropdown" style="left:0; right:auto;">
                    <ul class="dropdown__menu">
                      <li class="dropdown__item" @click="navigateTo('/tenders')">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2zm-4 14H8v-2h2v2zm0-4H8V6h2v6zm4 4h-2v-2h2v2zm0-4h-2V6h2v6z" />
                        </svg>
                        {{ $t('topBar.tenders') || 'Tenders' }}
                      </li>
                      <li class="dropdown__item" @click="navigateTo('/my-applications')">
                        <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                          <path fill="currentColor" d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                        </svg>
                        {{ $t('topBar.tenderApplications') || 'Tender Applications' }}
                      </li>
                    </ul>
                  </div>
                </transition>
              </li>
            </PermissionGuard>
          </ul>
        </nav>        <!-- Project breadcrumb navigation when in project view -->
        <nav class="app-breadcrumb" v-if="isAuthenticated && isInProjectView">
          <div class="breadcrumb-container">
            <button @click="goToUserHome" class="breadcrumb-item breadcrumb-home" :title="$t('topBar.dashboard')">
              <svg viewBox="0 0 20 20" width="16" height="16">
                <path fill="currentColor" d="M10 2L3 9v11h4v-6h6v6h4V9l-7-7z" />
              </svg>
              <span class="hide-mobile">{{ $t('topBar.dashboard') }}</span>
            </button>
            <svg class="breadcrumb-separator" viewBox="0 0 24 24" width="14" height="14">
              <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            <PermissionGuard permission="ACCESS_PROJECT">
              <button @click="navigateTo('/projects')" class="breadcrumb-item" :title="$t('topBar.projects')">
                <svg viewBox="0 0 20 20" width="14" height="14">
                  <path fill="currentColor" d="M4 3h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2zm0 2v3h12V5H4zm0 5v5h5v-5H4zm7 0v5h5v-5h-5z" />
                </svg>
                <span class="hide-mobile">{{ $t('topBar.projects') }}</span>
              </button>
            </PermissionGuard>
            <svg class="breadcrumb-separator" viewBox="0 0 24 24" width="14" height="14">
              <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            <span class="breadcrumb-item breadcrumb-current" :title="currentProjectName">
              <svg viewBox="0 0 20 20" width="14" height="14">
                <path fill="currentColor" d="M3 7V5C3 3.89543 3.89543 3 5 3H9L11 5H19C20.1046 5 21 5.89543 21 7V8H3V7ZM3 8H21V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V8Z"/>
              </svg>
              <span>{{ currentProjectName }}</span>
            </span>
          </div>
        </nav>

        <!-- Tender breadcrumb navigation when in tender view -->
        <nav class="app-breadcrumb" v-if="isAuthenticated && isInTenderView">
          <div class="breadcrumb-container">
            <button @click="goToUserHome" class="breadcrumb-item breadcrumb-home" :title="$t('topBar.dashboard')">
              <svg viewBox="0 0 20 20" width="16" height="16">
                <path fill="currentColor" d="M10 2L3 9v11h4v-6h6v6h4V9l-7-7z" />
              </svg>
              <span class="hide-mobile">{{ $t('topBar.dashboard') }}</span>
            </button>
            <svg class="breadcrumb-separator" viewBox="0 0 24 24" width="14" height="14">
              <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            <PermissionGuard permission="ACCESS_TENDERS">
              <button @click="navigateTo('/tenders')" class="breadcrumb-item" :title="$t('topBar.tenders') || 'Tenders'">
                <svg viewBox="0 0 20 20" width="14" height="14">
                  <path fill="currentColor" d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2zm-4 14H8v-2h2v2zm0-4H8V6h2v6zm4 4h-2v-2h2v2zm0-4h-2V6h2v6z" />
                </svg>
                <span class="hide-mobile">{{ $t('topBar.tenders') || 'Tenders' }}</span>
              </button>
            </PermissionGuard>
            <svg class="breadcrumb-separator" viewBox="0 0 24 24" width="14" height="14">
              <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            <span class="breadcrumb-item breadcrumb-current" :title="currentTenderName">
              <svg viewBox="0 0 20 20" width="14" height="14">
                <path fill="currentColor" d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2zm-4 14H8v-2h2v2zm0-4H8V6h2v6zm4 4h-2v-2h2v2zm0-4h-2V6h2v6z" />
              </svg>
              <span>{{ currentTenderName }}</span>
            </span>
          </div>
        </nav>

        <!-- Application breadcrumb navigation when in application view -->
        <nav class="app-breadcrumb" v-if="isAuthenticated && isInApplicationView">
          <div class="breadcrumb-container">
            <button @click="goToUserHome" class="breadcrumb-item breadcrumb-home" :title="$t('topBar.dashboard')">
              <svg viewBox="0 0 20 20" width="16" height="16">
                <path fill="currentColor" d="M10 2L3 9v11h4v-6h6v6h4V9l-7-7z" />
              </svg>
              <span class="hide-mobile">{{ $t('topBar.dashboard') }}</span>
            </button>
            <svg class="breadcrumb-separator" viewBox="0 0 24 24" width="14" height="14">
              <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            <PermissionGuard permission="ACCESS_TENDER_APPLICATIONS">
              <button @click="navigateTo('/my-applications')" class="breadcrumb-item" :title="$t('topBar.tenderApplications') || 'My Applications'">
                <svg viewBox="0 0 20 20" width="14" height="14">
                  <path fill="currentColor" d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </svg>
                <span class="hide-mobile">{{ $t('topBar.tenderApplications') || 'My Applications' }}</span>
              </button>
            </PermissionGuard>
            <svg class="breadcrumb-separator" viewBox="0 0 24 24" width="14" height="14">
              <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            <span class="breadcrumb-item breadcrumb-current" :title="currentApplicationName">
              <svg viewBox="0 0 20 20" width="14" height="14">
                <path fill="currentColor" d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
              </svg>
              <span>{{ currentApplicationName }}</span>
            </span>
          </div>
        </nav>
      </div>
      
      <div class="app-header__right">
        <!-- Notification icon component -->
        <NotificationIcon v-if="isAuthenticated" />
        
        <!-- Language selector component -->
        <LanguageSelector />
        
        <!-- Loading indicator -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
        </div>
          <!-- Login button for unauthenticated users -->
        <app-button v-if="!isAuthenticated" variant="primary" @click="login">
          <template #icon-left>
            <svg viewBox="0 0 20 20" width="16" height="16">
              <path fill="currentColor" d="M3 3a2 2 0 012-2h.5a.5.5 0 010 1H5a1 1 0 00-1 1v12a1 1 0 001 1h.5a.5.5 0 010 1H5a2 2 0 01-2-2V3zM13.5 7a.5.5 0 01.5.5v1a.5.5 0 01-.5.5H8.207l1.646 1.646a.5.5 0 01-.708.708l-2.5-2.5a.5.5 0 010-.708l2.5-2.5a.5.5 0 01.708.708L8.207 7H13.5z"/>
            </svg>
          </template>
          {{ $t('auth.signInBtn') }}
        </app-button>
        
        <!-- Show user profile if authenticated -->
        <div v-else class="app-header__profile">          <button class="app-header__profile-button" @click="toggleMenu" aria-label="User menu">
            <div v-if="user" class="app-header__avatar">
              <span>{{ userInitials }}</span>
            </div>
            <span class="app-header__username hide-mobile">{{ user?.lastName || 'User' }}</span>
            <svg class="app-header__dropdown-icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <transition name="dropdown-menu">
            <div v-if="menuVisible" class="dropdown">
              <ul class="dropdown__menu">
                <li class="dropdown__header">
                  <div class="app-header__avatar app-header__avatar--large">
                    <span>{{ userInitials }}</span>
                  </div>
                  <div class="dropdown__user-info">
                    <span class="dropdown__user-name">{{ user?.name || 'User' }}</span>
                    <span class="dropdown__user-email">{{ user?.email || 'No email' }}</span>
                  </div>
                </li>
                <li class="dropdown__divider"></li>
                <li @click="goToUserHome" class="dropdown__item">
                  <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                  </svg>
                  {{ $t('topBar.dashboard') }}
                </li>
                <PermissionGuard permission="ACCESS_PROJECT">
                  <li @click="navigateTo('/projects')" class="dropdown__item">
                    <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14h-2v-4h-2v4H8v-7h2v2h2v-2h2v7zm3-9h-8V6h8v2z" />
                    </svg>
                    {{ $t('topBar.projects') }}
                  </li>
                </PermissionGuard>
                <PermissionGuard permission="ADMIN">
                  <li @click="navigateTo('/admin/subscriptions')" class="dropdown__item">
                    <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    {{ $t('topBar.subscriptions') || 'Subscriptions' }}
                  </li>
                  <li @click="navigateTo('/admin/contact-requests')" class="dropdown__item">
                    <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M3 6h18v12H3z" />
                    </svg>
                    {{ $t('admin.contactRequests.menu') || 'Contact Requests' }}
                  </li>
                  <li @click="navigateTo('/admin/users')" class="dropdown__item">
                    <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5C23 14.17 18.33 13 16 13z"/>
                    </svg>
                    {{ $t('topBar.users') || 'Users' }}
                  </li>
                  <li @click="createFirmWithData" class="dropdown__item" :class="{ 'dropdown__item--loading': creatingFirm }">
                    <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z" />
                    </svg>
                    {{ creatingFirm ? $t('admin.testData.creating') : $t('admin.testData.createFirm') }}
                  </li>
                </PermissionGuard>
                <li @click="goToSettings" class="dropdown__item">
                  <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                  </svg>
                  {{ $t('topBar.settings') }}
                </li>
                <li v-if="user?.type === 'FIRM_OWNER'" @click="navigateTo('/my-subscription')" class="dropdown__item">
                  <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M12 2a7 7 0 100 14 7 7 0 000-14zm0 2a5 5 0 110 10A5 5 0 0112 4zM4 20v-1a7 7 0 0114 0v1H4z" />
                  </svg>
                  {{ $t('mySubscription.title') }}
                </li>
                <li @click="logout" class="dropdown__item dropdown__item--danger">
                  <svg class="dropdown__icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                  </svg>
                  {{ $t('topBar.signOut') }}
                </li>
              </ul>
            </div>
          </transition>
        </div>      </div>
    </div>

    <!-- Mobile Navigation Overlay -->
    <transition name="mobile-menu">
      <div v-if="mobileMenuOpen && isAuthenticated && !isInProjectView && !isInTenderView && !isInApplicationView" class="mobile-nav-overlay">
        <nav class="mobile-nav">
          <ul class="mobile-nav__list">
            <li class="mobile-nav__item" :class="{ 'mobile-nav__item--active': isDashboardActive }">
              <a @click.prevent="goToUserHome" class="mobile-nav__link">
                <div class="mobile-nav__icon">
                  <svg viewBox="0 0 20 20" width="20" height="20">
                    <path fill="currentColor" d="M10 2L3 9v11h4v-6h6v6h4V9l-7-7z" />
                  </svg>
                </div>
                <span class="mobile-nav__text">{{ $t('topBar.dashboard') }}</span>
              </a>
            </li>
            <PermissionGuard permission="ACCESS_PROJECT">
              <li class="mobile-nav__item" :class="{ 'mobile-nav__item--active': $route.path === '/projects' }">
                <a @click.prevent="navigateTo('/projects')" class="mobile-nav__link">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="20" height="20">
                      <path fill="currentColor" d="M4 3h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2zm0 2v3h12V5H4zm0 5v5h5v-5H4zm7 0v5h5v-5h-5z" />
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ $t('topBar.projects') }}</span>
                </a>
              </li>
            </PermissionGuard>
            <PermissionGuard permission="ADMIN">
              <li class="mobile-nav__item">
                <a @click.prevent="navigateTo('/admin/subscriptions')" class="mobile-nav__link">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ $t('topBar.subscriptions') || 'Subscriptions' }}</span>
                </a>
              </li>
              <li class="mobile-nav__item">
                <a @click.prevent="navigateTo('/admin/contact-requests')" class="mobile-nav__link">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M3 6h14v8H3z" />
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ $t('admin.contactRequests.menu') }}</span>
                </a>
              </li>
              <li class="mobile-nav__item">
                <a @click.prevent="navigateTo('/admin/users')" class="mobile-nav__link">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5C23 14.17 18.33 13 16 13z"/>
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ $t('topBar.users') || 'Users' }}</span>
                </a>
              </li>
              <li class="mobile-nav__item">
                <a @click.prevent="createFirmWithData" class="mobile-nav__link">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="18" height="18">
                      <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z" />
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ creatingFirm ? $t('admin.testData.creating') : $t('admin.testData.createFirm') }}</span>
                </a>
              </li>
            </PermissionGuard>
            <PermissionGuard permission="ACCESS_TENDERS">
              <li class="mobile-nav__item" :class="{ 'mobile-nav__item--active': $route.path.includes('/tenders') || $route.path.includes('/my-applications') }">
                <a @click.prevent="navigateTo('/tenders')" class="mobile-nav__link">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="20" height="20">
                      <path fill="currentColor" d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2zm-4 14H8v-2h2v2zm0-4H8V6h2v6zm4 4h-2v-2h2v2zm0-4h-2V6h2v6z" />
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ $t('topBar.tenders') || 'Tenders' }}</span>
                </a>
              </li>
              <li class="mobile-nav__item" :class="{ 'mobile-nav__item--active': $route.path.includes('/my-applications') }">
                <a @click.prevent="navigateTo('/my-applications')" class="mobile-nav__link" style="padding-left: 3rem;">
                  <div class="mobile-nav__icon">
                    <svg viewBox="0 0 20 20" width="20" height="20">
                      <path fill="currentColor" d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                    </svg>
                  </div>
                  <span class="mobile-nav__text">{{ $t('topBar.tenderApplications') || 'Tender Applications' }}</span>
                </a>
              </li>
            </PermissionGuard>
          </ul>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore, useUiStore, useProjectStore } from '@/stores'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { User } from '@/types'

// Import tender stores (with try-catch for graceful fallback)
let useTenderStore: any = null
let useTenderApplicationStore: any = null

try {
  const tenderStoreModule = require('@/stores/tenderStore')
  useTenderStore = tenderStoreModule.useTenderStore
} catch (e) {
  console.warn('TenderStore not available')
}

try {
  const applicationStoreModule = require('@/stores/tenderApplicationStore')
  useTenderApplicationStore = applicationStoreModule.useTenderApplicationStore
} catch (e) {
  console.warn('TenderApplicationStore not available')
}

// Services
import archiWebAuthService from '../services/archiweb.auth.service'
import permissionService from '../services/permission.service'
import { useNotifications } from '@/composables/useNotifications'
import axios from 'axios'

// Components
import LanguageSelector from './common/LanguageSelector.vue'
import NotificationIcon from './notifications/NotificationIcon.vue'
import AppButton from './base/AppButton.vue'
import PermissionGuard from './common/PermissionGuard.vue'

// Store and router
const authStore = useAuthStore()
const uiStore = useUiStore()
const projectStore = useProjectStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

// Initialize tender and application stores
const tenderStore = useTenderStore ? useTenderStore() : null
const applicationStore = useTenderApplicationStore ? useTenderApplicationStore() : null
const { notifySuccess, notifyError } = useNotifications()

// Reactive data
const menuVisible = ref(false)
const adminMenuVisible = ref(false)
const tendersMenuVisible = ref(false)
const errorMessage = ref<string | null>(null)
const mobileMenuOpen = ref(false)

// Computed properties
const user = computed((): User | null => authStore.getUser)
const isAuthenticated = computed((): boolean => authStore.isAuthenticated)
const isLoading = computed((): boolean => authStore.loading)
// expose `loading` for template compatibility (some templates reference `loading`)
const loading = isLoading
const getError = computed((): string | null => uiStore.error)
const getCurrentProjectName = computed((): string => projectStore.currentProject?.name || '')

const userInitials = computed((): string => {
  if (!user.value || !user.value.firstName) return '?'
  const nameParts = [user.value.firstName, user.value.lastName].filter(Boolean)
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
  }
  return nameParts[0][0].toUpperCase()
})

// Check if we're currently in a project view
const isInProjectView = computed((): boolean => {
  return route.name === 'ProjectDetails' && Boolean(route.params.projectId)
})

// Check if we're currently in a tender view
const isInTenderView = computed((): boolean => {
  return route.name === 'TenderDetails' && Boolean(route.params.id)
})

// Check if we're currently in a tender application view
const isInApplicationView = computed((): boolean => {
  return route.name === 'ApplicationDetails' && Boolean(route.params.id)
})

const isDashboardActive = computed((): boolean => {
  // Dashboard is active for home route OR admin dashboard route
  try {
    return route.path === '/' || route.path === '/admin'
  } catch (e) {
    return false
  }
})

const isAdminActive = computed((): boolean => {
  // Active when current path is under /admin BUT NOT the admin dashboard itself
  // Admin dropdown should only be active on admin sub-pages, not on /admin dashboard
  try {
    return adminMenuVisible.value || (route.path.startsWith('/admin') && route.path !== '/admin')
  } catch (e) {
    return adminMenuVisible.value
  }
})

const isTendersActive = computed((): boolean => {
  // Active when current path includes tenders or tender applications or when tenders menu is open
  try {
    return tendersMenuVisible.value || route.path.includes('/tenders') || route.path.includes('/my-applications')
  } catch (e) {
    return tendersMenuVisible.value
  }
})

// Get current project name from store
const currentProjectName = computed((): string => {
  if (!isInProjectView.value) return ''

  // Get project name from store
  const projectName = getCurrentProjectName.value
  if (projectName) {
    return projectName
  }

  // Fallback to project ID if name not available
  return (route.params.projectId as string) || 'Project'
})

// Get current tender name from store
const currentTenderName = computed((): string => {
  if (!isInTenderView.value) return ''

  // Get tender name from store
  const tender = tenderStore?.getCurrentTender || tenderStore?.currentTender
  if (tender) {
    return tender.title || `Tender ${tender.referenceNumber || tender.id}`
  }

  // Fallback to tender ID if name not available
  return `Tender ${route.params.id}` || 'Tender'
})

// Get current application name from store
const currentApplicationName = computed((): string => {
  if (!isInApplicationView.value) return ''

  // Get application name from store
  const application = applicationStore?.selectedApplication || applicationStore?.currentApplication
  if (application) {
    return `Application ${application.referenceNumber || application.id}`
  }

  // Fallback to application ID if name not available
  return `Application ${route.params.id}` || 'Application'
})

// Methods
const toggleMenu = (): void => {
  menuVisible.value = !menuVisible.value
}

const toggleAdminMenu = (): void => {
  adminMenuVisible.value = !adminMenuVisible.value
}

const toggleTendersMenu = (): void => {
  tendersMenuVisible.value = !tendersMenuVisible.value
}

const closeMenu = (): void => {
  menuVisible.value = false
}

const toggleMobileMenu = (): void => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const login = async (): Promise<void> => {
  try {
    // For OAuth login, we don't need credentials - redirect to auth server
    await archiWebAuthService.login()
  } catch (error) {
    console.error('Login error:', error)
    uiStore.setError('Failed to start login process')
  }
}

const goToHome = (): void => {
  router.push('/')
  closeMenu()
}

const goToUserHome = (): void => {
  // Redirect based on user type
  const userType = user.value?.type
  if (userType === 'FIRM_OWNER' || userType === 'FIRM_MEMBER') {
    router.push('/firms')
  } else if (userType === 'CLIENT') {
    router.push('/firms/client-dashboard')
  } else if (userType === 'ADMIN') {
    // Admin users redirect to admin dashboard
    router.push('/admin')
  } else {
    // Fallback for any other user types
    router.push('/firms/client-dashboard')
  }
  closeMenu()
  mobileMenuOpen.value = false
}

const navigateTo = (path: string): void => {
  console.log('Navigating to:', path)
  console.log('User:', user.value)
  console.log('User type:', user.value?.type)
  console.log('Is admin check:', user.value?.type === 'ADMIN')
  router.push(path)
  closeMenu()
  adminMenuVisible.value = false
  tendersMenuVisible.value = false
  mobileMenuOpen.value = false
}

const goToSettings = (): void => {
  router.push('/settings')
  closeMenu()
}

const creatingFirm = ref(false)
const createFirmWithData = async (): Promise<void> => {
  if (creatingFirm.value) return
  creatingFirm.value = true
  adminMenuVisible.value = false
  mobileMenuOpen.value = false
  try {
    await axios.get('/api/test-data/create-firm-with-data')
    notifySuccess(t('admin.testData.createFirmSuccess'))
  } catch (e) {
    notifyError(t('admin.testData.createFirmError'))
  } finally {
    creatingFirm.value = false
  }
}

const logout = (): void => {
  // Use a custom modal instead of the browser's confirm dialog
  uiStore.setModal({
    title: t('auth.signOutConfirmTitle'),
    message: t('auth.signOutConfirmMessage'),
    confirmText: t('auth.confirmSignOut'),
    cancelText: t('auth.cancelSignOut'),
    onConfirm: () => {
      authStore.logout()
      closeMenu()
      uiStore.setModal(null)
    }
  })
}

const handleClickOutside = (event: MouseEvent): void => {
  // Close profile menu if clicked outside
  const profileContainer = document.querySelector('.app-header__profile')
  if (profileContainer && !profileContainer.contains(event.target as Node) && menuVisible.value) {
    closeMenu()
  }

  // Close admin menu if clicked outside (target only the admin-specific elements)
  const adminMenu = document.querySelector('.admin-nav-item')
  const adminDropdown = document.querySelector('.admin-dropdown')
  if (adminMenu && adminDropdown && !adminMenu.contains(event.target as Node) && adminMenuVisible.value) {
    adminMenuVisible.value = false
  }

  // Close tenders menu if clicked outside (target only the tenders-specific elements)
  const tendersMenu = document.querySelector('.tenders-nav-item')
  const tendersDropdown = document.querySelector('.tenders-dropdown')
  if (tendersMenu && tendersDropdown && !tendersMenu.contains(event.target as Node) && tendersMenuVisible.value) {
    tendersMenuVisible.value = false
  }

  // Close mobile menu if clicked on overlay
  const mobileNavOverlay = document.querySelector('.mobile-nav-overlay')
  const mobileNav = document.querySelector('.mobile-nav')
  if (mobileNavOverlay && mobileNavOverlay.contains(event.target as Node) &&
      (!mobileNav || !mobileNav.contains(event.target as Node)) && mobileMenuOpen.value) {
    mobileMenuOpen.value = false
  }
}

// Watchers
watch(getError, (newError: string | null) => {
  if (newError) {
    errorMessage.value = typeof newError === 'string' ? newError : 'An error occurred'
    // Clear error after 5 seconds
    setTimeout(() => {
      errorMessage.value = null
      uiStore.setError(null)
    }, 5000)
  }
})

watch(isAuthenticated, (newValue: boolean) => {
  if (newValue && !user.value) {
    authStore.fetchUser()
  }
})

watch(route, () => {
  closeMenu()
  mobileMenuOpen.value = false
})

// Lifecycle hooks
onMounted(() => {
  // Close menu when clicking outside
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  // Clean up event listener
  document.removeEventListener('click', handleClickOutside)
})
</script><style scoped>
:root {
  --top-bar-height: 72px;
}

/* Header component using enhanced professional design */
.app-header {
  position: fixed;
  top: var(--trial-banner-offset, 0px);
  left: 0;
  right: 0;
  height: var(--top-bar-height);
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 25%, #93c5fd 50%, #bfdbfe 75%, #dbeafe 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  z-index: var(--z-fixed, 1030); /* Use CSS variable for consistent z-index management */
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.app-header__container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  padding: 0 var(--space-8);
  height: 100%;
  position: relative;
  z-index: 1;
}

.app-header__left {
  display: flex;
  align-items: center;
  gap: var(--space-8);
}

.app-header__right {
  display: flex;
  align-items: center;
  gap: var(--space-6);
}

/* Enhanced Logo Design */
.app-header__logo {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  cursor: pointer;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  background: none;
  transition: all var(--transition-fast) ease;
  border: none;
  position: relative;
}

.app-header__logo:hover {
  transform: translateY(-1px);
}

.app-header__logo-icon {
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.app-header__logo-img {
  width: 150px;
  height: 56px;
  max-height: calc(var(--top-bar-height) - 16px);
  object-fit: contain;
  display: block;
  border-radius: inherit;
}

@media (max-width: 768px) {
  .app-header__logo-img {
    width: 120px;
    height: 44px;
    max-height: 44px;
  }
}

@media (max-width: 480px) {
  .app-header__logo-img {
    width: 100px;
    height: 36px;
    max-height: 36px;
  }
}

.app-header__logo-text {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);  letter-spacing: -0.5px;
  color: white;
}

/* Enhanced Navigation */
.app-nav {
  height: 100%;
}

.app-nav__list {
  display: flex;
  height: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: var(--space-3);
}

.app-nav__item {
  height: 100%;
  position: relative;
}

.app-nav__link {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 var(--space-6);
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  transition: all var(--transition-fast) ease;
  position: relative;
  gap: var(--space-3);
  cursor: pointer;
}

.app-nav__link:hover {
  color: white;
}

.app-nav__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  transition: all var(--transition-fast) ease;
}

.app-nav__text {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
}

.app-nav__item--active .app-nav__link {
  color: white;
  font-weight: var(--font-weight-bold);
}

.app-nav__item--active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 4px;  background: linear-gradient(90deg, #f59e0b, #f97316);
  border-radius: var(--radius-full);
}

/* Enhanced Profile section */
.app-header__profile {
  position: relative;
}

.app-header__profile-button {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3) var(--space-5);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  position: relative;
}

.app-header__profile-button:hover {
  background: transparent;
  color: white;
}

.app-header__avatar {
  width: 38px;
  height: 38px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 1;
}

.app-header__avatar--large {
  width: 64px;
  height: 64px;
  margin-right: var(--space-4);
  flex-shrink: 0;
  border: 3px solid white;
  font-size: var(--font-size-base);
}

.app-header__username {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  position: relative;
  z-index: 1;
}

.app-header__dropdown-icon {
  transition: all var(--transition-fast) ease;
  opacity: 0.8;
  position: relative;
  z-index: 1;
}

.app-header__profile-button:hover .app-header__dropdown-icon {
  opacity: 1;
  transform: rotate(180deg);
}

/* Enhanced Dropdown menu */
.dropdown {
  position: absolute;
  top: calc(100% + var(--space-4));
  right: 0;
  z-index: 1050;
}

.dropdown__menu {
  width: 360px;
  background: white;
  border-radius: var(--radius-2xl);
  list-style: none;
  padding: 0;
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--neutral-200);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.dropdown__item {
  padding: var(--space-5) var(--space-6);
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  display: flex;
  align-items: center;
  color: var(--neutral-700);
  font-weight: var(--font-weight-medium);
  border-left: 3px solid transparent;
}

.dropdown__item:hover {
  background: linear-gradient(135deg, var(--neutral-50), var(--neutral-100));
  transform: translateX(6px);
  border-left-color: var(--primary);
  color: var(--neutral-800);
}

.dropdown__item--danger {
  color: var(--error) !important;
  border-top: 1px solid rgba(239, 68, 68, 0.1);
  background: rgba(239, 68, 68, 0.03);
}

.dropdown__item--danger:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(239, 68, 68, 0.1)) !important;
  border-left-color: var(--error);
}

.dropdown__header {
  padding: var(--space-8) var(--space-6) !important;
  cursor: default !important;
  display: flex !important;
  align-items: flex-start !important;
  border-bottom: 2px solid var(--neutral-100);
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
}

.dropdown__header:hover {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9) !important;
  transform: none !important;
  border-left-color: transparent !important;
}

.dropdown__user-info {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.dropdown__user-name {
  font-weight: var(--font-weight-bold);
  color: var(--neutral-900);
  margin-bottom: var(--space-2);
  font-size: var(--font-size-lg);
}

.dropdown__user-email {
  font-size: var(--font-size-sm);
  color: var(--neutral-600);
  word-break: break-all;
}

.dropdown__divider {
  height: 2px;
  background: linear-gradient(90deg, var(--neutral-200), var(--neutral-100), var(--neutral-200));
  margin: 0;
  padding: 0 !important;
  cursor: default !important;
}

.dropdown__icon {
  margin-right: var(--space-5);
  color: var(--neutral-500);
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  transition: all var(--transition-fast) ease;
}

.dropdown__item:hover .dropdown__icon {
  color: var(--primary);
  transform: scale(1.1);
}

.dropdown__item--danger .dropdown__icon {
  color: var(--error);
}

.dropdown__item--danger:hover .dropdown__icon {
  transform: scale(1.1);
}

/* Breadcrumb Navigation */
.app-breadcrumb {
  display: flex;
  align-items: center;
  height: 100%;
}

.breadcrumb-container {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  color: rgba(255, 255, 255, 0.8);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  font-size: var(--font-size-sm);
  font-weight: 500;
  text-decoration: none;
}

.breadcrumb-item:hover {
  background: transparent;
  color: white;
}

.breadcrumb-item.breadcrumb-home {
  color: rgba(255, 255, 255, 0.85);
}

.breadcrumb-item.breadcrumb-home:hover {
  color: white;
}

.breadcrumb-item.breadcrumb-current {
  color: white;
  font-weight: 600;
  cursor: default;
}

.breadcrumb-item.breadcrumb-current:hover {
  color: white;
}

.breadcrumb-separator {
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.breadcrumb-item svg {
  flex-shrink: 0;
  opacity: 0.9;
}

.breadcrumb-item:hover svg {
  opacity: 1;
}

/* Enhanced responsive design for breadcrumb */
@media (max-width: 768px) {
  .breadcrumb-container {
    gap: var(--space-1);
  }
  
  .breadcrumb-item {
    gap: var(--space-1);
    padding: var(--space-1);
    font-size: var(--font-size-xs);
  }
  
  .breadcrumb-separator {
    width: 10px;
    height: 10px;
  }
}

@media (max-width: 480px) {
  .breadcrumb-container {
    gap: 2px;
  }
  
  .breadcrumb-item {
    padding: 2px 4px;
  }
  
  /* Hide text on very small screens, show only icons */
  .breadcrumb-item span.hide-mobile {
    display: none;
  }
}

/* Loading spinner */
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all var(--transition-base) ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-menu-enter-active,
.dropdown-menu-leave-active {
  transition: all var(--transition-base) ease;
  transform-origin: top right;
}

.dropdown-menu-enter-from,
.dropdown-menu-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

/* Responsive design */
@media (max-width: 768px) {
  :root {
    --top-bar-height: var(--top-bar-height-mobile, 56px);
  }

  .app-header {
    height: var(--top-bar-height-mobile, 56px);
  }
  
  .app-header__container {
    padding: 0 var(--space-3);
  }
  
  .app-header__left {
    gap: var(--space-2);
    flex-shrink: 1;
    min-width: 0;
  }

  .app-header__logo {
    padding: var(--space-1) var(--space-2);
  }
  
  .app-header__logo-text {
    font-size: var(--font-size-base);
  }

  .app-header__right {
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .app-header__profile-button {
    padding: var(--space-2) var(--space-2);
    gap: var(--space-2);
  }

  .app-header__avatar {
    width: 32px;
    height: 32px;
    font-size: var(--font-size-xs);
  }
  
  .dropdown {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    z-index: 1050;
  }
  
  .dropdown__menu {
    width: 100%;
    max-width: 100%;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
}

@media (max-width: 480px) {
  .app-header__container {
    padding: 0 var(--space-2);
  }

  .app-header__right {
    gap: var(--space-1);
  }
  
  .app-header__profile-button {
    padding: var(--space-1) var(--space-2);
    gap: var(--space-1);
  }
  
  .app-header__avatar {
    width: 28px;
    height: 28px;
    font-size: var(--font-size-xs);
  }
}

/* Mobile Menu Button */
.mobile-menu-button {
  display: none;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast) ease;
}

.mobile-menu-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Mobile Navigation Overlay */
.mobile-nav-overlay {
  position: fixed;
  top: calc(var(--top-bar-height-mobile, 56px) + var(--trial-banner-offset, 0px));
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  backdrop-filter: blur(4px);
}

.mobile-nav {
  background: white;
  border-radius: 0 0 var(--radius-2xl) var(--radius-2xl);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: 80vh;
}

.mobile-nav__list {
  list-style: none;
  margin: 0;
  padding: var(--space-4) 0;
}

.mobile-nav__item {
  margin: 0;
}

.mobile-nav__link {
  display: flex;
  align-items: center;
  padding: var(--space-6) var(--space-8);
  color: var(--neutral-700);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  transition: all var(--transition-fast) ease;
  gap: var(--space-5);
  cursor: pointer;
  border-left: 4px solid transparent;
}

.mobile-nav__link:hover {
  background: linear-gradient(135deg, var(--neutral-50), var(--neutral-100));
  color: var(--primary);
  border-left-color: var(--primary);
  transform: translateX(8px);
}

.mobile-nav__item--active .mobile-nav__link {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(59, 130, 246, 0.1));
  color: var(--primary);
  border-left-color: var(--primary);
  font-weight: var(--font-weight-bold);
}

.mobile-nav__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--neutral-100);
  border-radius: var(--radius-lg);
  color: var(--neutral-600);
  transition: all var(--transition-fast) ease;
  flex-shrink: 0;
}

.mobile-nav__link:hover .mobile-nav__icon {
  background: var(--primary);
  color: white;
  transform: scale(1.1);
}

.mobile-nav__item--active .mobile-nav__icon {
  background: var(--primary);
  color: white;
}

.mobile-nav__text {
  font-weight: inherit;
}

/* Mobile Menu Transitions */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all var(--transition-base) ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}

.mobile-menu-enter-from .mobile-nav {
  transform: translateY(-100%);
}

.mobile-menu-leave-to .mobile-nav {
  transform: translateY(-100%);
}

/* Utility classes */
.hide-mobile {
  display: block;
}

.hide-desktop {
  display: none;
}

.show-mobile {
  display: none;
}

@media (max-width: 768px) {
  .hide-mobile {
    display: none;
  }
  
  .hide-desktop {
    display: block;
  }

  .show-mobile {
    display: block;
  }

  .mobile-menu-button {
    display: block;
  }
}

/* Add space for fixed header */
:deep(#app) {
  padding-top: var(--top-bar-height);
}
</style>
